<template>
    <div v-dark class="quiz-scores">
        <div v-dark class="quiz-scores__head">
            <div v-dark class="quiz-scores__title">
                <Icon type="barChart" />
                <h2>Quiz Scores</h2>
            </div>
        </div>
        <div class="quiz-scores__stats">
            <div
                v-dark
                class="quiz-scores__stat"
                :aria-label="`${ orderedQuizzes.length } Quizzes`"
            >
                <div
                    v-dark
                    class="quiz-scores__stat-number"
                    aria-hidden="true"
                >
                    {{ orderedQuizzes.length }}
                </div>
                <div
                    v-dark
                    class="quiz-scores__stat-label"
                    aria-hidden="true"
                >
                    Quizzes
                </div>
            </div>
            <div
                v-dark
                class="quiz-scores__stat"
                :aria-label="`${ numDaysStudying } Day${ numDaysStudying !== 1 ? 's': '' } Studying`"
            >
                <div
                    v-dark
                    class="quiz-scores__stat-number"
                    aria-hidden="true"
                >
                    {{ numDaysStudying }}
                </div>
                <div
                    v-dark
                    class="quiz-scores__stat-label"
                    aria-hidden="true"
                >
                    Day{{ numDaysStudying !== 1 ? 's': '' }} Studying
                </div>
            </div>
        </div>
        <template v-if="!isShowingTextDescription">
            <div
                tabindex="-1"
                class="quiz-scores__chart-container"
                role="region"
                aria-label="Quiz scores graph, summary text description below"
            >
                <Bar
                    :key="`${quizScores.length}_${isDarkMode}`"
                    type="percent"
                    class="quiz-scores__bar"
                    size="small"
                    :bar-color="barColor"
                    :is-dark-mode="isDarkMode"
                    :hide-average-line="true"
                    :data="quizScores"
                    description="Graph of quiz scores by quiz"
                />
                <div class="quiz-scores__bar-x-labels">
                    <div v-dark class="quiz-scores__bar-x-label">
                        {{ chartXLabels.left }}
                    </div>
                    <div v-dark class="quiz-scores__bar-x-label">
                        {{ chartXLabels.right }}
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="isShowingTextDescription && orderedQuizzes.length > 0">
            <div
                ref="quizScoresTextDescriptionRef"
                class="quiz-scores__text-description"
                tabindex="-1"
            >
                {{ textDescriptionDateRange }},
                {{ textDescriptionScoreSummary }}.
            </div>
        </template>
        <template v-else-if="isShowingTextDescription && !orderedQuizzes.length">
            <div class="quiz-scores__text-description">
                You haven't taken a quiz yet. Quiz scores will display here when you take a quiz.
            </div>
        </template>
        <div
            v-dark
            tabindex="0"
            class="quiz-scores__text-toggle"
            :class="{
                'quiz-scores__text-toggle--active': isShowingTextDescription,
            }"
            role="switch"
            aria-label="Text Description"
            :aria-checked="isShowingTextDescription ? 'true' : 'false'"
            @focus="isShowingTextDescriptionTooltip = true"
            @blur="isShowingTextDescriptionTooltip = false"
            @mouseenter="isShowingTextDescriptionTooltip = true"
            @mouseleave="isShowingTextDescriptionTooltip = false"
            @click="toggleTextDescription"
            @keydown.enter="toggleTextDescription"
            @mousedown.prevent
        >
            <Tooltip
                v-if="isShowingTextDescriptionTooltip"
                :theme="breakpoint === 'black-bear' ? 'rightalign' : undefined"
                :is-dark-mode="isDarkMode"
            >
                Text Description
            </Tooltip>
            <Icon class="quiz-scores__text-toggle-icon" type="text" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { userModule } from '@/store/user/module'
import { quizModule } from '@/store/quiz/module'
import { getScoreRange, months } from '@/utils'
import { screenModule } from '@/store/screen/module'

const {
    Icon,
    Bar,
    Tooltip,
} = UIKit

const isShowingTextDescriptionTooltip = ref(false)
const isShowingTextDescription = ref(false)
const quizScoresTextDescriptionRef = ref<HTMLElement | null>(null)

const orderedQuizzes = computed(() => {
    return [ ...quizModule.state.quizzes ]
        .filter((q) => {
            return (q.mode !== -1 && q.mode !== 5)
        })
        .sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        })
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const quizScores = computed(() => {
    if (!orderedQuizzes.value.length) {
        return [
            { y: 26 },
            { y: 31 },
            { y: 36 },
            { y: 41 },
            { y: 47 },
            { y: 51 },
            { y: 57 },
            { y: 67 },
            { y: 75 },
            { y: 82 },
            { y: 89 },
            { y: 97 },
        ]
    }

    return orderedQuizzes.value
        .reduce((acc, q) => {
            if (!q.answers.length) {
                return acc
            }

            const dateObj = new Date(q.createdAt)
            const tooltipDate = formatTooltipDate(dateObj)
            const score = Math.round((q.correctCount / (q.correctCount + q.incorrectCount)) * 100)
            const hoverColor = getBarHoverColor(score)
            if (hoverColor) {
                acc.push({
                    y: score,
                    states: {
                        hover: {
                            color: hoverColor,
                            borderColor: hoverColor,
                        },
                    },
                    info: tooltipDate,
                })
            }

            return acc
        }, [] as { y: number; info: string; states: { hover: { color: string; borderColor: string } } }[])
})

const maxMinScoreInfo = computed(() => {
    return orderedQuizzes.value.reduce((acc, q) => {
        const score = Math.round((q.correctCount / (q.correctCount + q.incorrectCount)) * 100)
        if (!acc.max || score > acc.max.score) {
            acc.max = {
                score,
                date: new Date(q.createdAt),
            }
        }
        if (!acc.min || score < acc.min.score) {
            acc.min = {
                score,
                date: new Date(q.createdAt),
            }
        }
        return acc
    }, { max: null, min: null } as Record<'max' | 'min', { score: number; date: Date } | null>)
})

const firstQuizDate = computed(() => {
    return orderedQuizzes.value.length
        ? new Date(orderedQuizzes.value[0].createdAt)
        : null
})

const lastQuizDate = computed(() => {
    return orderedQuizzes.value.length
        ? new Date(orderedQuizzes.value[orderedQuizzes.value.length - 1].createdAt)
        : null
})

const textDescriptionDateRange = computed(() => {
    if (!firstQuizDate.value || !lastQuizDate.value) {
        return ''
    }

    if (numDaysStudying.value === 1) {
        return `On ${fullDate(firstQuizDate.value)}`
    }

    return `From ${fullDate(firstQuizDate.value)} through ${fullDate(lastQuizDate.value)}`
})

const textDescriptionScoreSummary = computed(() => {
    if (!maxMinScoreInfo.value.max || !maxMinScoreInfo.value.min) {
        return ''
    }

    if (orderedQuizzes.value.length === 1) {
        return `your score was ${maxMinScoreInfo.value.max.score}%`
    }

    const uniqueScores = new Set(quizScores.value.map((s) => s.y))
    if (uniqueScores.size === 1 || (numDaysStudying.value === 1)) {
        return `your highest score was ${
            maxMinScoreInfo.value.max.score
        }% and your lowest score was ${
            maxMinScoreInfo.value.min.score
        }%`
    }

    return `your highest score was ${
        maxMinScoreInfo.value.max.score
    }% on ${
        fullDate(maxMinScoreInfo.value.max.date)
    }, and your lowest score was ${
        maxMinScoreInfo.value.min.score
    }% on ${
        fullDate(maxMinScoreInfo.value.min.date)
    }`
})

const numDaysStudying = computed(() => {
    if (!firstQuizDate.value || !lastQuizDate.value) {
        return 0
    }

    const startOfFirstQuizDate = new Date(
        firstQuizDate.value.getFullYear(),
        firstQuizDate.value.getMonth(),
        firstQuizDate.value.getDate()
    )

    const numDays = Math.ceil((lastQuizDate.value.getTime() - startOfFirstQuizDate.getTime()) / 86400000)
    return numDays
})

const shouldShowLabelYears = computed(() => {
    const firstDateYear = firstQuizDate.value?.getFullYear()

    if (firstDateYear && firstDateYear !== new Date().getFullYear()) {
        return true
    } else {
        return false
    }
})

const chartXLabels = computed(() => {
    const firstDate = firstQuizDate.value
    const lastDate = lastQuizDate.value

    if (!firstDate || !lastDate) {
        return {
            left: 'Oldest',
            right: 'Most Recent',
        }
    }

    return {
        left: `${months[firstDate.getMonth()]?.slice(0, 3)} ${firstDate.getDate()}${
            shouldShowLabelYears.value ? `, ${firstDate.getFullYear()}` : ''
        }`,
        right: `${months[lastDate.getMonth()]?.slice(0, 3)} ${lastDate.getDate()}${
            shouldShowLabelYears.value ? `, ${lastDate.getFullYear()}` : ''
        }`,
    }
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const barColor = computed(() => {
    return orderedQuizzes.value.length
        ? undefined
        : isDarkMode.value
            ? breakpoint.value === 'black-bear'
                ? BrandColors.charcoal
                : BrandColors.moonlitOcean
            : BrandColors.grayProgressBackground
})

onMounted(() => {
    userModule.actions.fetchUserData()
})

const getBarHoverColor = (score: number) => {
    const scoreRange = getScoreRange(score)
    return !isDarkMode.value
        ? scoreRange === 'low'
            ? BrandColors.tomato
            : scoreRange === 'medium'
                ? BrandColors.cheddar
                : BrandColors.field
        : scoreRange === 'low'
            ? BrandColors.brandRed
            : scoreRange === 'medium'
                ? BrandColors.bananaBread
                : BrandColors.jungleGreen
}

const formatTooltipDate = (date: Date) => {
    const day = ('0' + String(date.getDate())).slice(-2)
    const month = ('0' + String(date.getMonth() + 1)).slice(-2)
    const year = date.getFullYear()
    return `${month}.${day}.${year}`
}

const fullDate = (date: Date) => {
    return `${
        months[date.getMonth()]
    } ${
        date.getDate()
    }, ${
        date.getFullYear()
    }`
}

const toggleTextDescription = () => {
    isShowingTextDescription.value = !isShowingTextDescription.value

    if (isShowingTextDescription.value) {
        setTimeout(() => {
            const textDescriptionEl = quizScoresTextDescriptionRef.value
            if (textDescriptionEl) {
                textDescriptionEl.focus()
            }
        }, 0)
    }
}
</script>

<style lang="scss" scoped>
.quiz-scores {
    background: $white;
    border-radius: 6px;
    padding: 3px 16px 10px;
    box-sizing: border-box;
    height: 410px;
    position: relative;

    @include breakpoint(brown-bear) {
        height: 430px;
    }

    @include breakpoint(black-bear) {
        height: 427px;
        padding: 4px 10px 0;
    }

    &--dark {
        background: rgba($white, 0.02);
        color: $fog;

        @include breakpoint(black-bear) {
            background: $brand-black;
        }
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 38px;
        border-bottom: 1px solid $gray-divider;
        padding-left: 8px;
        margin-bottom: 14px;

        &--dark {
            border-color: $pewter;
        }
    }

    &__title {
        display: flex;
        align-items: center;

        svg {
            height: 22px;
            margin-right: 8px;
            color: $slate;
        }

        h2 {
            font-size: 16px;
            line-height: 22px;
            font-weight: 600;
            margin: 0;
        }

        &--dark svg {
            color: $pewter;
        }
    }

    &__stats {
        display: flex;
        width: 100%;
        justify-content: space-between;
    }

    &__stat {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background-color: $pearl;
        border-radius: 6px;
        width: calc(50% - 6px);
        height: 65px;

        &--dark {
            background-color: $moonlit-ocean;

            @include breakpoint(black-bear) {
                background-color: $black-feather;
            }
        }

        @include breakpoint(brown-bear) {
            width: calc(50% - 8px);
        }

        @include breakpoint(black-bear) {
            width: calc(50% - 4px);
        }
    }

    &__stat-number {
        font-weight: 600;
        font-size: 26px;
        letter-spacing: 0.26px;
        line-height: 31px;
    }

    &__stat-label {
        font-size: 13px;
        line-height: 16px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__chart-container {
        outline: none;
    }

    &__bar {
        width: 100%;
        height: 196px;
        margin-top: 16px;

        @include breakpoint(brown-bear) {
            height: 202px;
        }
    }

    &__bar-x-labels {
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding: 6px 45px 0 1px;
        margin-bottom: 11px;
        box-sizing: border-box;
    }

    &__bar-x-label {
        font-size: 12px;
        line-height: 15px;
        letter-spacing: 0.3px;
        color: $slate-01;

        &--dark {
            opacity: 0.8;
            color: $white;
        }
    }

    &__text-description {
        box-sizing: border-box;
        padding: 24px 7px;
        height: 244px;
        line-height: 26px;
        outline: none;
        font-weight: 400;
    }

    &__text-toggle {
        display: inline-flex;
        cursor: pointer;
        color: $brand-blue;
        border-radius: 6px;
        outline: none;
        position: absolute;
        left: 16px;
        bottom: 16px;

        &:hover {
            color: $brand-black;
        }

        &:focus {
            &::after {
                content: '';
                position: absolute;
                left: -2px;
                top: -2px;
                width: calc(100% + 4px);
                height: calc(100% + 4px);
                box-sizing: border-box;
                border: 1px solid $brand-blue;
                border-radius: 8px;
            }
        }

        @include breakpoint(black-bear) {
            left: 10px;
            bottom: 12px;
        }

        &--dark {
            color: $banana-bread;

            &:hover {
                color: $butterscotch;
            }

            &:focus {
                &::after {
                    border-color: $butterscotch;
                }
            }
        }

        &--active {
            background-color: $brand-blue;
            color: $white;

            &:hover {
                background-color: $brand-black;
                color: $white;
            }

            &:focus {
                &::after {
                    left: -3px;
                    top: -3px;
                    width: calc(100% + 6px);
                    height: calc(100% + 6px);
                    border-radius: 9px;
                }
            }

            &--dark {
                background-color: $banana-bread;
                color: $brand-black;

                &:hover {
                    background-color: $butterscotch;
                    color: $brand-black;
                }
            }
        }

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: calc(-100% - 4px);

            @include breakpoint(brown-bear) {
                top: calc(-100% + 4px);
            }

            @include breakpoint(black-bear) {
                top: calc(-100% + 8px);
            }
        }
    }

    &__text-toggle-icon {
        @include breakpoint(brown-bear) {
            width: 36px;
            height: 36px;
        }

        @include breakpoint(black-bear) {
            width: 40px;
            height: 40px;
        }
    }
}
</style>
